// WallSolid.ts — PURE punchable wall builder for the Buildings wall layer (§4.2). The builder
// works on the two wall FACES: the inner ring (room-facing) and the outer ring (exterior). Each
// wall segment is a thin solid between them spanning `[wallBaseZ, wallTopZ]`. Openings
// (windows/doors) punch through the segment, and the builder re-emits the leftover material as a
// PANEL SPLIT:
//
//   - inner face / outer face: full-height piers in the gaps between openings, plus an
//     under-sill quad and an over-head quad within each opening's horizontal span;
//   - reveal faces per opening: two jamb quads (inner→outer, vertical), a sill-top quad, and a
//     head-underside quad, on the `revealMaterialSlot`;
//   - top cap (and optional bottom cap): the strip between inner and outer edges, split where an
//     opening reaches the wall top/bottom.
//
// Wall-placement convention (Buildings Set2 §2): under Set1 the outline was the INTERIOR face and
// the caller passed `OffsetOutward(footprint, t)` as the outer ring. Set2 makes the outline the
// wall CENTERLINE, so the caller passes `centerline ∓ t/2` (`Offset_Pure.CenterlineToWallRings`).
// This builder is agnostic to which convention produced the rings — it only ever sees two parallel
// rings — but the SET2 pairing is what makes the facing direction (and therefore the inner/outer
// face assignment) line up with the room interior.
//
// VERTEX PARAMETRIZATION (watertightness): each segment's wall surface is bilinear between the
// inner edge `inner[i]→inner[i+1]` and the outer edge `outer[i]→outer[i+1]`:
//     P(t, a, z) = lerp( lerp(inner[i],inner[i+1],t), lerp(outer[i],outer[i+1],t), a )
// where t ∈ [0,1] is the along fraction and a ∈ [0,1] is the across fraction (0 = inner, 1 =
// outer). At segment endpoints this yields EXACTLY the ring corner vertices (inner[i]/outer[i],
// inner[i+1]/outer[i+1]), so adjacent segments share identical vertices and the wall is watertight
// at every corner — the same guarantee a CSG backend would need to satisfy (§10).
//
// UVs are metre-scaled: u = along-wall distance (metres), v = across × wallThickness (metres) for
// faces, so brick/stone tile continuously across piers.
//
// Pure: no three / mobx-graphlink imports, so the Node vitest lane imports this directly. The
// plan (§4.2) defines the `WallSolidBuilder` interface so a CSG backend can be swapped in later.
import type {Vec2Simple} from "../../Utils/ThreeJS/VectorStructs.js";
import type {WallSegment} from "../../Server/DB/mapObjects/Building/BuildingGeometry.js";
import type {FloorWallSegment} from "./BuildingWalls_Pure.js";
import {MeshBuilder, type MeshPlan, type V3} from "./MeshPlan.js";

export type {WallSegment};

export interface WallSolidBuilder {
	readonly segments: ReadonlyArray<WallSegment>;
	punch(segmentIndex: number, alongCenter: number, width: number, zBottom: number, zTop: number,
	      revealMaterialSlot: string): void;
	build(): MeshPlan;
}

export type WallSolidBuilderOpts = {
	/** The room-facing wall face (CLOSED + CCW). Under Set2 §2 this is the room's CENTERLINE inset
	 *  by `wallThickness/2`. Must match the segment list. */
	innerRing: Vec2Simple[];
	/** The exterior wall face (CLOSED, same vertex order). Under Set2 §2 this is the room's
	 *  centerline offset outward by `wallThickness/2`. */
	outerRing: Vec2Simple[];
	wallBaseZ: number;
	wallTopZ: number;
	/** The outward wall thickness in metres (used for UV v-scaling). Defaults to the mean inner↔
	 *  outer distance. */
	wallThickness?: number;
	/** Emit the bottom cap (facing down). Default false — the floor slab hides it (§4.2). Set
	 *  true when building a standalone closed solid (e.g. the watertightness test). */
	emitBottom?: boolean;
};

type Span = {lo: number; hi: number; zBottom: number; zTop: number; revealSlot: string};

/** One wall strip to punch: its four corner points (inner/exterior faces at each end), the outward
 *  unit normal, and the along-wall length `punchLen` that `alongCenter`/`width` are measured
 *  against. Set2 F3 factored this out of the ring builder so a MERGED FLOOR SEGMENT (whose corners
 *  come from the merge engine's pre-mitered inner/outer endpoints rather than from two
 *  whole rings) can reuse the exact same panel-split — keeping the punch and the bound opening on
 *  one arc length (the F1 matched-pair invariant, doc §5). */
type WallStrip = {
	innerA: Vec2Simple; innerB: Vec2Simple;
	outerA: Vec2Simple; outerB: Vec2Simple;
	normal: Vec2Simple;
	/** The along length the punches are expressed against. For the ring builder this is the inner
	 *  edge length; for the F3 floor builder it is `FloorWallSegment.innerLength`. */
	punchLen: number;
};

/** Emit one wall strip (both faces with the punch panel-split, the reveals, and the caps) into `b`.
 *  This is the shared body of BOTH the ring-based and the merged-floor-segment-based builders.
 *  See `WallSolidBuilder_PanelSplit` for the parametrization + rectangular-opening notes. */
function EmitWallStrip(b: MeshBuilder, strip: WallStrip, spansIn: Span[], opts: {
	wallBaseZ: number; wallTopZ: number; thickness: number; emitBottom: boolean;
	/** Set2 F4: emit the cross-section face at this strip's `start` (resp. `end`) because that end is a T-JOIN — it butts into another wall's interior, so NO neighbouring segment closes it. Default false: a normal corner join is closed by the neighbour's own band, so a cap there would be a hidden interior face. */
	buttCapStart?: boolean; buttCapEnd?: boolean;
}): void {
	const innerA = strip.innerA, innerB = strip.innerB, outerA = strip.outerA, outerB = strip.outerB;
	const length = strip.punchLen;
	if (length < 1e-9) return;
	// Bilinear position function. t ∈ [0,1] along, a ∈ [0,1] across (0=inner,1=outer).
	// NOTE: `t` is a FRACTION measured against the (shorter) inner edge, so using it directly
	// on the (miter-slanted, longer) outer edge would WIDEN any opening toward the outside,
	// leaving a trapezoidal hole that a rectangular door-frame cannot fill (the "door frame
	// doesn't line up with the hole" bug). Openings must therefore be RECTANGULAR — we
	// sample the outer face with the OUTER edge's own arc-length (see `outerPt`/`overhang`).
	const P = (t: number, a: number, z: number): V3 => {
		const ix = innerA[0] + (innerB[0] - innerA[0]) * t;
		const iy = innerA[1] + (innerB[1] - innerA[1]) * t;
		const ox = outerA[0] + (outerB[0] - outerA[0]) * t;
		const oy = outerA[1] + (outerB[1] - outerA[1]) * t;
		const x = ix + (ox - ix) * a;
		const y = iy + (oy - iy) * a;
		return [x, y, z];
	};
	// Normal of the wall faces: the strip's outward normal (right of travel for CCW).
	const nrm: V3 = [strip.normal[0], strip.normal[1], 0];
	// Tangent direction (along the strip, for the jamb normals).
	const outerLenRaw = Math.hypot(outerB[0] - outerA[0], outerB[1] - outerA[1]);
	const tvec: V3 = [
		(innerB[0] - innerA[0]) / length, (innerB[1] - innerA[1]) / length, 0,
	];

	// ---- Rectangular-opening alignment (§4.2 fix). The mitered outer edge is LONGER than the
	// inner one (each corner adds `overhang` metres at each end). A perpendicular ray from an
	// inner point at arc `s` hits the outer face at outer arc `s + overhang`, where
	// `overhang = (outerLen - innerLen) / 2` for a symmetric (orthogonal) miter. Sampling the
	// outer face with THIS arc produces a rectangular (90°) opening whose outer jamb is the
	// perpendicular projection of the inner jamb — so it exactly matches the rectangular
	// door/window frame. Without this, the outer opening widens by `outerLen/innerLen`.
	const outerLen = outerLenRaw || length;
	const overhang = (outerLen - length) / 2;
	// Per-face position samplers, keyed by each face's OWN arc length (metres from its start).
	const innerPt = (s: number, z: number): V3 => P(Math.max(0, Math.min(1, s / length)), 0, z);
	const outerPt = (s: number, z: number): V3 => P(Math.max(0, Math.min(1, s / outerLen)), 1, z);

	// Sort + guard against overlaps (the opening layer already overlap-checks; clamp anyway).
	// Spans carry INNER arc lo/hi; the outer face shifts both bounds by `overhang`.
	const sorted = [...spansIn].sort((a, b2)=>a.lo - b2.lo);
	for (let k = 1; k < sorted.length; k++) {
		const prev = sorted[k - 1];
		if (sorted[k].lo < prev.hi) { sorted[k].lo = prev.hi; sorted[k].hi = Math.max(sorted[k].hi, prev.hi); }
	}
	const innerSpans = sorted;
	const outerSpans = sorted.map(sp=>({lo: sp.lo + overhang, hi: sp.hi + overhang, zBottom: sp.zBottom, zTop: sp.zTop, revealSlot: sp.revealSlot}));

	// Faces: inner (a=0, normal -nrm) and outer (a=1, normal +nrm).
	const st = {wallBaseZ: opts.wallBaseZ, wallTopZ: opts.wallTopZ, thickness: opts.thickness};
	EmitFace(b, st, innerSpans, innerPt, nrm, -1, 0, length);
	EmitFace(b, st, outerSpans, outerPt, nrm, +1, 1, outerLen);

	// Reveals per opening (connect inner arc `s` to outer arc `s + overhang` → rectangular).
	for (const sp of sorted) EmitReveals(b, st, sp, innerPt, outerPt, overhang, tvec);

	// Top cap (normal +z) split where an opening reaches the top.
	EmitCap(b, st, sorted, innerPt, outerPt, overhang, opts.wallTopZ, +1, length, outerLen, sp=>sp.zTop >= opts.wallTopZ - 1e-6);
	// Bottom cap (normal -z), optional.
	if (opts.emitBottom) {
		EmitCap(b, st, sorted, innerPt, outerPt, overhang, opts.wallBaseZ, -1, length, outerLen, sp=>sp.zBottom <= opts.wallBaseZ + 1e-6);
	}

	// Set2 F4 — BUTT CAPS at T-join ends: an end butting into another wall's interior has no neighbouring segment, so without a cross-section face the band is open there (a see-through hole wherever the ending band is not fully embedded in the through band, e.g. a thick interior wall meeting a thin one).
	// The cap spans the ending strip's MITERED cross-section, NOT the flat ±t/2 rectangle: floor strips carry end-mitered corners (F3 deviation 1) and the neighbour reaches exactly those corners, so a flat cap leaves slivers wherever two walls differ in thickness. A collinear sub-span is NOT flagged as a T, so a coplanar interior cap can never appear inside a merged band.
	if (opts.buttCapStart) EmitButtCap(b, st, innerPt, outerPt, 0, tvec, -1, sorted);
	if (opts.buttCapEnd) EmitButtCap(b, st, innerPt, outerPt, length, tvec, +1, sorted);
}

/** One end cross-section (a "butt cap") of a wall strip: the face spanning the strip's interior→exterior faces at one end, normal along `∓tangent` (`side` = −1 at the start, +1 at the end) i.e. pointing OUT of the band — so it is front-facing from outside and `FrontSide` culls it from inside the band, where it is not visible. Split vertically where an opening fills the storey height, so it never bridges the punch. */
function EmitButtCap(b: MeshBuilder, ctx: StripCtx, innerPt: (s: number, z: number)=>V3,
                     outerPt: (s: number, z: number)=>V3, arc: number, tvec: V3, side: number,
                     spans: Span[]): void {
	const n: V3 = [tvec[0] * side, tvec[1] * side, 0];
	// Vertical split intervals: [base, top] minus the openings covering this end's arc.
	const cuts: Array<[number, number]> = [];
	let cursor = ctx.wallBaseZ;
	for (const sp of spans) {
		if (arc < sp.lo - 1e-9 || arc > sp.hi + 1e-9) continue;
		if (sp.zBottom > cursor + 1e-9) cuts.push([cursor, sp.zBottom]);
		cursor = Math.max(cursor, sp.zTop);
	}
	if (cursor < ctx.wallTopZ - 1e-9) cuts.push([cursor, ctx.wallTopZ]);
	b.setMaterialSlot("wall");
	for (const [z0, z1] of cuts) {
		if (z1 - z0 < 1e-9) continue;
		const P00 = innerPt(arc, z0), P10 = outerPt(arc, z0);
		const P11 = outerPt(arc, z1), P01 = innerPt(arc, z1);
		b.quad(P00, P10, P11, P01, n, [0, z0], [ctx.thickness, z0], [ctx.thickness, z1], [0, z1]);
	}
}

/** The z/thickness context shared by the per-strip emitters. */
type StripCtx = {wallBaseZ: number, wallTopZ: number, thickness: number};

/** Emit one wall face with the punch panel-split. `pt(s,z)` samples the face at arc `s`
 *  (metres from the strip's start on THAT face) and height z. `spans` carry arc bounds
 *  already expressed in this face's coordinate system. `aFace` is 0 (inner) or 1 (outer);
 *  `normalSign` is -1 (inner, normal = -nrm) or +1 (outer). */
function EmitFace(b: MeshBuilder, ctx: StripCtx, spans: Span[], pt: (s: number, z: number)=>V3, nrm: V3,
                  normalSign: number, aFace: number, arcLen: number): void {
	// Full-height piers = intervals of [0,arcLen] not covered by any opening.
	const bounds = new Set<number>([0, arcLen]);
	for (const sp of spans) { bounds.add(sp.lo); bounds.add(sp.hi); }
	const sorted = [...bounds].sort((x, y)=>x - y);
	for (let k = 0; k < sorted.length - 1; k++) {
		const s = sorted[k], e = sorted[k + 1];
		if (e - s < 1e-9) continue;
		if (InOpening(spans, (s + e) / 2)) continue;
		EmitFullHeightQuad(b, ctx, pt, s, e, nrm, normalSign, aFace);
	}
	// Under-sill + over-head quads within each opening's horizontal span.
	for (const sp of spans) {
		if (sp.zBottom > ctx.wallBaseZ + 1e-6) {
			EmitBoundedQuad(b, ctx, pt, sp.lo, sp.hi, ctx.wallBaseZ, sp.zBottom, nrm, normalSign, aFace);
		}
		if (sp.zTop < ctx.wallTopZ - 1e-6) {
			EmitBoundedQuad(b, ctx, pt, sp.lo, sp.hi, sp.zTop, ctx.wallTopZ, nrm, normalSign, aFace);
		}
	}
}

function InOpening(spans: Span[], at: number): boolean {
	return spans.some(sp=>at >= sp.lo - 1e-9 && at <= sp.hi + 1e-9);
}

/** Full-height pier quad: arc [a,bb] (metres on this face), z [wallBase, wallTop]. */
function EmitFullHeightQuad(b: MeshBuilder, ctx: StripCtx, pt: (s: number, z: number)=>V3,
                            a: number, bb: number, nrm: V3, normalSign: number, aFace: number): void {
	if (bb - a < 1e-9) return;
	const A = pt(a, ctx.wallBaseZ);
	const B = pt(bb, ctx.wallBaseZ);
	const C = pt(bb, ctx.wallTopZ);
	const D = pt(a, ctx.wallTopZ);
	const n: V3 = [nrm[0] * normalSign, nrm[1] * normalSign, 0];
	const vAcross = aFace * ctx.thickness;
	b.setMaterialSlot("wall");
	b.quad(A, B, C, D, n, [a, vAcross], [bb, vAcross], [bb, ctx.wallTopZ], [a, ctx.wallTopZ]);
}

/** Bounded quad (under-sill or over-head): arc [a,bb], z [z0,z1]. */
function EmitBoundedQuad(b: MeshBuilder, ctx: StripCtx, pt: (s: number, z: number)=>V3,
                         a: number, bb: number, z0: number, z1: number, nrm: V3, normalSign: number, aFace: number): void {
	if (bb - a < 1e-9 || z1 - z0 < 1e-9) return;
	const A = pt(a, z0);
	const B = pt(bb, z0);
	const C = pt(bb, z1);
	const D = pt(a, z1);
	const n: V3 = [nrm[0] * normalSign, nrm[1] * normalSign, 0];
	const vAcross = aFace * ctx.thickness;
	b.setMaterialSlot("wall");
	b.quad(A, B, C, D, n, [a, vAcross], [bb, vAcross], [bb, z1], [a, z1]);
}

/** Reveal faces (jambs + sill top + head underside) for one opening. The jamb at inner arc
 *  `lo` (outer arc `lo+overhang`) has normal −tangent; the jamb at `hi` has normal +tangent.
 *  Sill top faces up; head faces down. `innerPt`/`outerPt` sample each face by its own arc, so
 *  the jamb is the PERPENDICULAR projection → the opening is a rectangular 90° prism (matches
 *  the door/window frame exactly). */
function EmitReveals(b: MeshBuilder, ctx: StripCtx, sp: Span, innerPt: (s: number, z: number)=>V3,
                     outerPt: (s: number, z: number)=>V3, overhang: number,
                     tvec: V3): void {
	const zBottom = sp.zBottom, zTop = sp.zTop;
	const loO = sp.lo + overhang, hiO = sp.hi + overhang;
	b.setMaterialSlot(sp.revealSlot);
	// Left jamb (inner lo → outer lo+overhang): normal −tangent.
	const A = innerPt(sp.lo, zBottom), B = outerPt(loO, zBottom);
	const C = outerPt(loO, zTop), D = innerPt(sp.lo, zTop);
	b.quad(A, B, C, D, [-tvec[0], -tvec[1], 0], [zBottom, 0], [zBottom, ctx.thickness], [zTop, ctx.thickness], [zTop, 0]);
	// Right jamb (inner hi → outer hi+overhang): normal +tangent.
	const A2 = innerPt(sp.hi, zBottom), B2 = outerPt(hiO, zBottom);
	const C2 = outerPt(hiO, zTop), D2 = innerPt(sp.hi, zTop);
	b.quad(A2, B2, C2, D2, [tvec[0], tvec[1], 0], [zBottom, 0], [zBottom, ctx.thickness], [zTop, ctx.thickness], [zTop, 0]);
	// Sill top (normal +z).
	const S1 = innerPt(sp.lo, zBottom), S2 = outerPt(loO, zBottom);
	const S3 = outerPt(hiO, zBottom), S4 = innerPt(sp.hi, zBottom);
	b.quad(S1, S2, S3, S4, [0, 0, 1], [0, 0], [0, ctx.thickness], [ctx.thickness, ctx.thickness], [ctx.thickness, 0]);
	// Head underside (normal -z).
	const H1 = innerPt(sp.lo, zTop), H2 = outerPt(loO, zTop);
	const H3 = outerPt(hiO, zTop), H4 = innerPt(sp.hi, zTop);
	b.quad(H1, H2, H3, H4, [0, 0, -1], [0, 0], [0, ctx.thickness], [ctx.thickness, ctx.thickness], [ctx.thickness, 0]);
}

/** The top (or bottom) cap strip between inner and outer edges at z = `capZ`, split at
 *  intervals where `shouldSplit(sp)` is true. `normalSign` +1 = up (top), -1 = down. */
function EmitCap(b: MeshBuilder, ctx: StripCtx, spans: Span[], innerPt: (s: number, z: number)=>V3,
                 outerPt: (s: number, z: number)=>V3, overhang: number,
                 capZ: number, normalSign: number, length: number, outerLen: number,
                 shouldSplit: (sp: Span)=>boolean): void {
	const splitSpans = spans.filter(sp=>shouldSplit(sp)).sort((a, bb)=>a.lo - bb.lo);
	let cursor = 0;
	b.setMaterialSlot("wall");
	for (const sp of splitSpans) {
		if (sp.lo > cursor + 1e-9) {
			EmitCapInterval(b, ctx, innerPt, outerPt, overhang, cursor, sp.lo, capZ, normalSign, length, outerLen);
		}
		cursor = Math.max(cursor, sp.hi);
	}
	if (cursor < length - 1e-9) {
		EmitCapInterval(b, ctx, innerPt, outerPt, overhang, cursor, length, capZ, normalSign, length, outerLen);
	}
}

function EmitCapInterval(b: MeshBuilder, ctx: StripCtx, innerPt: (s: number, z: number)=>V3,
                         outerPt: (s: number, z: number)=>V3, overhang: number,
                         a: number, bb: number, capZ: number, normalSign: number,
                         length: number, outerLen: number): void {
	if (bb - a < 1e-9) return;
	// Inner edge at inner arc [a,bb]. The outer edge is the PERPENDICULAR projection of the
	// inner (outer arc = inner arc + overhang) so the cap stays rectangular at opening splits;
	// but a cap interval that touches a WALL END (a==0 or bb==length) must reach the true
	// outer corner (outer arc 0 / outerLen), since only the full-length cap reaches the
	// miter corners and openings never do (they keep jambMargin from the ends).
	const oA = a <= 1e-9 ? 0 : a + overhang;
	const oB = bb >= length - 1e-9 ? outerLen : bb + overhang;
	const A = innerPt(a, capZ);
	const B = innerPt(bb, capZ);
	const C = outerPt(oB, capZ);
	const D = outerPt(oA, capZ);
	const n: V3 = [0, 0, normalSign];
	b.quad(A, B, C, D, n, [a, 0], [bb, 0], [bb, ctx.thickness], [a, ctx.thickness]);
}

/** The panel-split `WallSolidBuilder`. See `WallSolidBuilderOpts`. */
export class WallSolidBuilder_PanelSplit implements WallSolidBuilder {
	readonly segments: WallSegment[];
	private readonly strips: WallStrip[];
	private readonly wallBaseZ: number;
	private readonly wallTopZ: number;
	private readonly thickness: number;
	private readonly emitBottom: boolean;
	private readonly punches: Map<number, Span[]> = new Map();

	constructor(opts: WallSolidBuilderOpts) {
		this.wallBaseZ = opts.wallBaseZ;
		this.wallTopZ = opts.wallTopZ;
		this.emitBottom = opts.emitBottom ?? false;
		this.thickness = opts.wallThickness ?? EstimateThickness(opts.innerRing, opts.outerRing);
		this.segments = BuildSegmentsFromRings(opts.innerRing, opts.outerRing);
		this.strips = WallStripsFromRings(opts.innerRing, opts.outerRing, this.segments);
	}

	punch(segmentIndex: number, alongCenter: number, width: number, zBottom: number, zTop: number,
	      revealMaterialSlot: string): void {
		let spans = this.punches.get(segmentIndex);
		if (spans == null) { spans = []; this.punches.set(segmentIndex, spans); }
		spans.push({lo: alongCenter - width / 2, hi: alongCenter + width / 2, zBottom, zTop, revealSlot: revealMaterialSlot});
	}

	build(): MeshPlan {
		const b = new MeshBuilder();
		for (let i = 0; i < this.strips.length; i++) {
			EmitWallStrip(b, this.strips[i], this.punches.get(i) ?? [], {
				wallBaseZ: this.wallBaseZ, wallTopZ: this.wallTopZ, thickness: this.thickness, emitBottom: this.emitBottom,
			});
		}
		return b.build();
	}
}

/** Build the per-segment strips for a pair of parallel rings (index i spans inner[i]→inner[i+1],
 *  wrapping to 0). */
function WallStripsFromRings(innerRing: Vec2Simple[], outerRing: Vec2Simple[], segments: WallSegment[]): WallStrip[] {
	const n = Math.min(innerRing.length, outerRing.length);
	const out: WallStrip[] = [];
	for (let i = 0; i < segments.length; i++) {
		const seg = segments[i];
		out.push({
			innerA: innerRing[i], innerB: innerRing[(i + 1) % n],
			outerA: outerRing[i], outerB: outerRing[(i + 1) % n],
			normal: seg.normal,
			punchLen: seg.length,
		});
	}
	return out;
}


/** Estimate the outward wall thickness as the mean inner↔outer ring vertex distance. */
function EstimateThickness(innerRing: Vec2Simple[], outerRing: Vec2Simple[]): number {
	const n = Math.min(innerRing.length, outerRing.length);
	let sum = 0, cnt = 0;
	for (let i = 0; i < n; i++) {
		const a = innerRing[i], bb = outerRing[i];
		if (a == null || bb == null) continue;
		sum += Math.hypot(bb[0] - a[0], bb[1] - a[1]);
		cnt++;
	}
	return cnt ? sum / cnt : 0.2;
}

/** Build the `WallSegment` list from parallel inner/outer rings (index i spans inner[i]->inner[i+1], wrapping to 0). Both rings are OPEN (no trailing duplicate). Carries the INNER face's start/end + outward normal; `length` is the inner edge length. `punch`/`build` interpret along/width against these inner-face segments. Set2 §2: `innerRing` is `centerline − t/2` and `outerRing` is `centerline + t/2`; the INNER ring is what the opening layer binds and clamps against (`ResolveRoomWallSegments`), so alongCenter/width mean the same thing on the punched face and in the bound opening. */
export function BuildSegmentsFromRings(innerRing: Vec2Simple[], outerRing: Vec2Simple[]): WallSegment[] {
	const segs: WallSegment[] = [];
	const n = Math.min(innerRing.length, outerRing.length);
	for (let i = 0; i < n; i++) {
		const start = innerRing[i];
		const end = innerRing[(i + 1) % n];
		const dx = end[0] - start[0];
		const dy = end[1] - start[1];
		const length = Math.hypot(dx, dy);
		if (length < 1e-9) continue;
		// outward normal (right of travel for CCW interior-on-left)
		const normal: Vec2Simple = [dy / length, -dx / length];
		segs.push({start, end, length, normal});
	}
	return segs;
}

/** Create a `WallSolidBuilder` backed by the panel-split implementation. See `WallSolidBuilderOpts`. */
export function CreateWallSolidBuilder_PanelSplit(opts: WallSolidBuilderOpts): WallSolidBuilder {
	return new WallSolidBuilder_PanelSplit(opts);
}

// ---------------------------------------------------------------------------
// Set2 F3 — the MERGED FLOOR WALL builder
// ---------------------------------------------------------------------------
/** A punch instruction for `WallSolidBuilder_FloorSegments.punch`: the FLOOR segment index plus the
 *  opening's along/length against that segment's INTERIOR face (`FloorWallSegment.innerLength`). */
export type FloorPunch = {
	segmentIndex: number;
	alongCenter: number;
	width: number;
	zBottom: number;
	zTop: number;
	revealMaterialSlot: string;
};

/** Set2 F3 (doc §6.1): builds the FLOOR's wall solid from the merge engine's `FloorWallSegment[]`
 *  — one strip per MERGED segment, using the segment's PRE-MITERED `innerStart/innerEnd/
 *  outerStart/outerEnd` (F2 output; the builder must NOT re-offset them) and interpreting
 *  `alongCenter`/`width` against the segment's INTERIOR face (`innerLength`).
 *
 *  Ring-vs-segment note (doc §5, F1 deviation 2): the F1 wall was built from a room's whole inner
 *  ring, so the punch and the bound opening shared ONE arc length. Under F3 the opening is bound to
 *  a merged FLOOR segment, so this builder consumes the SAME per-segment `innerLength` the binder
 *  used and the ring-based builder would agree — hence `ResolveOpeningsForFloor` and this builder
 *  are a MATCHED PAIR and must be changed together. This builder delegates to `EmitWallStrip`, the
 *  identical panel-split the ring builder uses, so there is exactly one implementation of the punch.
 *
 *  `segments` are assumed to have UNIQUE inner-face extents (the merge engine collapses coincident
 *  edges, so a shared wall is one segment; two claimants' overlapping bands would z-fight, which is
 *  precisely what the merge removes). */
export class WallSolidBuilder_FloorSegments implements WallSolidBuilder {
	readonly segments: WallSegment[];
	private readonly planSegments: FloorWallSegment[];
	private readonly strips: WallStrip[];
	private readonly wallBaseZ: number;
	private readonly wallTopZ: number;
	private readonly emitBottom: boolean;
	private readonly punches: Map<number, Span[]> = new Map();

	constructor(opts: {
		segments: FloorWallSegment[];
		/** z (relative to the floor plane) at/above which the walls start/stop. */
		wallBaseZ: number; wallTopZ: number;
		emitBottom?: boolean;
	}) {
		this.planSegments = opts.segments;
		this.wallBaseZ = opts.wallBaseZ;
		this.wallTopZ = opts.wallTopZ;
		this.emitBottom = opts.emitBottom ?? false;
		// A `WallSegment` per floor segment (start/end = the CENTERLINE endpoints), so
		// `segments[i]` stays index-aligned with `planSegments[i]` for callers that index it.
		this.segments = opts.segments.map(s=>({start: s.start, end: s.end, length: s.length, normal: s.normal}));
		this.strips = opts.segments.map(s=>({
			innerA: s.innerStart, innerB: s.innerEnd,
			outerA: s.outerStart, outerB: s.outerEnd,
			normal: s.normal,
			// The punch is measured on the INTERIOR face (the face the openings were bound against).
			punchLen: s.innerLength,
		}));
	}

	punch(segmentIndex: number, alongCenter: number, width: number, zBottom: number, zTop: number,
	      revealMaterialSlot: string): void {
		let spans = this.punches.get(segmentIndex);
		if (spans == null) { spans = []; this.punches.set(segmentIndex, spans); }
		spans.push({lo: alongCenter - width / 2, hi: alongCenter + width / 2, zBottom, zTop, revealSlot: revealMaterialSlot});
	}

	build(): MeshPlan {
		const b = new MeshBuilder();
		for (let i = 0; i < this.strips.length; i++) {
			const thickness = this.planSegments[i]?.wallThickness ?? 0.2;
			const seg = this.planSegments[i];
			EmitWallStrip(b, this.strips[i], this.punches.get(i) ?? [], {
				wallBaseZ: this.wallBaseZ, wallTopZ: this.wallTopZ, thickness, emitBottom: this.emitBottom,
				// Set2 F4: a T-join end has no neighbouring segment to close its cross-section, so emit the
				// butt cap there. A normal corner join must NOT be capped — the neighbour already closes it.
				buttCapStart: seg?.tJunctionStarts ?? false,
				buttCapEnd: seg?.tJunctionEnds ?? false,
			});
		}
		return b.build();
	}
}
